import { FormEvent, useEffect, useState } from "react";

import { updateEntityCategories } from "@/api/category";
import { createEntity, editEntity } from "@/api/entity";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { EntityType } from "@/types/form/entity";

import { useEntityModalStore } from "@/hooks/store/use-entity-modal-store";

import { getCategoryIds } from "@/utils/category";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Modal from "@/components/ui/modal";
import { Textarea } from "@/components/ui/textarea";

import CategoryMultiSelect from "../category-multi-select";

const EntityModal = () => {
  const queryClient = useQueryClient();

  const { isOpen, onClose, entity } = useEntityModalStore();
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<number[]>(() =>
    getCategoryIds(entity)
  );

  useEffect(() => {
    if (isOpen) {
      setSelectedCategoryIds(getCategoryIds(entity));
      return;
    }

    setSelectedCategoryIds([]);
  }, [entity, isOpen]);

  const { mutate, isPending } = useMutation({
    mutationFn: async (data: EntityType) => {
      const response = entity
        ? await editEntity(data)
        : await createEntity(data);
      const targetId = entity?.id ?? response?.data?.id;

      if (targetId && selectedCategoryIds.length) {
        await updateEntityCategories(targetId, selectedCategoryIds);
      }

      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["entities"] });
      onClose();
    }
  });

  const titleText = entity
    ? `ویرایش جدول ${entity.previewName}`
    : "ساخت جدول جدید";
  const submitBtnText = entity ? "ویرایش جدول" : "ساخت جدول جدید";

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const newData: Record<string, any> = {
      id: entity?.id
    };

    formData.forEach((value, key) => {
      newData[key] = value;
    });

    mutate(newData as EntityType);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} description="" title={titleText}>
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="flex flex-col gap-y-1">
          <label htmlFor="" className="text-sm">
            نام نمایشی
          </label>
          <Input
            type="text"
            name="previewName"
            defaultValue={entity?.previewName ?? ""}
            placeholder="مثال: مدیریت کاربران"
            required
          />
        </div>
        <div className="flex flex-col gap-y-1">
          <label htmlFor="" className="text-sm">
            نام جدول
          </label>
          <Input
            type="text"
            name="tableName"
            defaultValue={entity?.tableName ?? ""}
            placeholder="مثال: Users"
            required
            pattern="[A-Za-z]+"
            title="فقط حروف انگلیسی مجاز است"
          />
          <span className="text-[10px] text-slate-500">
            فقط حروف انگلیسی مجاز است
          </span>
        </div>
        <div className="flex flex-col gap-y-1">
          <label htmlFor="" className="text-sm">
            توضیحات
          </label>
          <Textarea
            name="description"
            defaultValue={entity?.description ?? ""}
            placeholder="مثال: این جدول برای ذخیره اطلاعات حساب کاربری استفاده می‌شود"
            className="resize-none"
          />
        </div>
        <CategoryMultiSelect
          value={selectedCategoryIds}
          onChange={setSelectedCategoryIds}
          title="دسته بندی های جدول"
          description="یک یا چند دسته بندی برای جدول انتخاب کنید"
        />
        <Button type="submit" className="w-full" disabled={isPending}>
          {submitBtnText}
        </Button>
      </form>
    </Modal>
  );
};

export default EntityModal;
